/**
 * --------------------------------------------------------------------------
 * CoreUI PRO calendar.js
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import BaseComponent from './base-component.js'
import EventHandler from './dom/event-handler.js'
import Manipulator from './dom/manipulator.js'
import SelectorEngine from './dom/selector-engine.js'
import { announce } from './util/announce.js'
import {
  escapeHtml, sanitizeByConfig, type SanitizerAllowList, SVGAllowlist
} from './util/sanitizer.js'
import {
  CHEVRON_DOUBLE_LEFT_ICON, CHEVRON_DOUBLE_RIGHT_ICON, CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON
} from './util/icons.js'
import {
  defineJQueryPlugin, getUID, isRTL, jQueryDispatch
} from './util/index.js'
import {
  type CalendarKeyAction,
  type CalendarKeyContext,
  constrainDate,
  convertToDateObject,
  convertToLimitDate,
  convertToSelectionDate,
  convertToShownDate,
  createDate,
  createDateFormatter,
  createGroupsInArray,
  type DisabledDate,
  formatCellName,
  formatSelectionName,
  formatWeekName,
  formatYearsRange,
  getCalendarDate,
  getCalendarKeyAction,
  getKeptDay,
  getClosestSelectable,
  getDateBySelectionType,
  getMonthDetails,
  getMonthsNames,
  getStartOfView,
  getStartOfWeek,
  getWeekLabel,
  getWeekNumberName,
  getWeekRowDate,
  getYears,
  isCellDisabled,
  isCellOutsideLimits,
  isDateDisabled,
  isDateInRange,
  isDateSelected,
  isDisableDateInRange,
  isPeriodDisabled,
  isPeriodInRange,
  isPeriodSelected,
  isToday,
  isYearBeforeMonth,
  type KeptDay,
  parseToDateString,
  type PeriodViewTypes,
  type SelectionTypes,
  setRovingTabIndex,
  setTimeFromDate,
  type ViewTypes,
  YEARS_PER_PAGE
} from './util/calendar.js'

/**
 * Constants
 */

const NAME = 'calendar'
const DATA_KEY = 'coreui.calendar'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const EVENT_BLUR = `blur${EVENT_KEY}`
const EVENT_CALENDAR_DATE_CHANGE = `calendarDateChange${EVENT_KEY}`
const EVENT_CALENDAR_MOUSE_LEAVE = `calendarMouseleave${EVENT_KEY}`
const EVENT_CALENDAR_VIEW_CHANGE = `calendarViewChange${EVENT_KEY}`
const EVENT_END_DATE_CHANGE = `endDateChange${EVENT_KEY}`
const EVENT_FOCUS = `focus${EVENT_KEY}`
const EVENT_KEYDOWN = `keydown${EVENT_KEY}`
const EVENT_SELECT_END_CHANGE = `selectEndChange${EVENT_KEY}`
const EVENT_START_DATE_CHANGE = `startDateChange${EVENT_KEY}`
const EVENT_MOUSEENTER = `mouseenter${EVENT_KEY}`
const EVENT_MOUSELEAVE = `mouseleave${EVENT_KEY}`
const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_CALENDAR_CELL = 'calendar-cell'
const CLASS_NAME_CALENDAR_CELL_INNER = 'calendar-cell-inner'
const CLASS_NAME_CALENDAR_ROW = 'calendar-row'
const CLASS_NAME_CALENDARS = 'calendars'
const CLASS_NAME_SHOW_WEEK_NUMBERS = 'show-week-numbers'
const CLASS_NAME_VISUALLY_HIDDEN = 'visually-hidden'

const SELECTOR_BTN_DOUBLE_NEXT = '.btn-double-next'
const SELECTOR_BTN_DOUBLE_PREV = '.btn-double-prev'
const SELECTOR_BTN_MONTH = '.btn-month'
const SELECTOR_BTN_NEXT = '.btn-next'
const SELECTOR_BTN_PREV = '.btn-prev'
const SELECTOR_BTN_YEAR = '.btn-year'
const SELECTOR_CALENDAR = '.calendar'
const SELECTOR_CALENDAR_CELL = '.calendar-cell'
const SELECTOR_CALENDAR_CELL_FOCUSABLE = `${SELECTOR_CALENDAR_CELL}[tabindex]`
const SELECTOR_CALENDAR_CELL_SELECTABLE = `${SELECTOR_CALENDAR_CELL}[data-coreui-selectable]`
const SELECTOR_CALENDAR_ROW = '.calendar-row'
const SELECTOR_CALENDAR_ROW_FOCUSABLE = `${SELECTOR_CALENDAR_ROW}[tabindex]`
const SELECTOR_CALENDAR_ROW_SELECTABLE = `${SELECTOR_CALENDAR_ROW}[data-coreui-selectable]`
const SELECTOR_DATA_CALENDAR = '[data-coreui-calendar]'

const CELL_RENDERERS: Record<ViewTypes, keyof CalendarConfig> = {
  days: 'renderDayCell',
  months: 'renderMonthCell',
  quarters: 'renderQuarterCell',
  years: 'renderYearCell'
}

const VIEW_BY_SELECTION_TYPE: Record<string, ViewTypes> = {
  day: 'days',
  week: 'days',
  month: 'months',
  quarter: 'quarters',
  year: 'years'
}

type CalendarCellMeta = {
  isDisabled: boolean
  isInRange: boolean
  isSelected: boolean
  label: string
}

type CalendarDayCellMeta = CalendarCellMeta & {
  isInCurrentMonth: boolean
  isToday: boolean
}

type CalendarConfig = {
  allowList: SanitizerAllowList
  ariaNavNextMonthLabel: string
  ariaNavNextYearLabel: string
  ariaNavNextYearsLabel: string
  ariaNavPrevMonthLabel: string
  ariaNavPrevYearLabel: string
  ariaNavPrevYearsLabel: string
  ariaNothingToPickLabel: string | null
  ariaSelectedDateAnnouncement: string | null
  ariaSelectedRangeAnnouncement: string | null
  ariaWeekNumberLabel: ((weekNumber: number) => string | undefined) | null
  calendarDate: Date | number | string | null
  calendars: number
  dayFormat: 'numeric' | '2-digit'
  disabledDates: DisabledDate | DisabledDate[] | null
  endDate: Date | number | string | null
  firstDayOfWeek: number
  locale: string
  maxDate: Date | number | string | null
  minDate: Date | number | string | null
  monthFormat: 'long' | 'narrow' | 'short' | 'numeric' | '2-digit'
  navNextDoubleIcon: string
  navNextIcon: string
  navPrevDoubleIcon: string
  navPrevIcon: string
  range: boolean
  renderDayCell: ((date: Date, meta?: CalendarDayCellMeta) => string) | null
  renderMonthCell: ((date: Date, meta: CalendarCellMeta) => string) | null
  renderQuarterCell: ((date: Date, meta: CalendarCellMeta) => string) | null
  renderYearCell: ((date: Date, meta: CalendarCellMeta) => string) | null
  sanitize: boolean
  sanitizeFn: ((unsafeHtml: string) => string) | null
  selectAdjacentDays: boolean
  selectEndDate: boolean
  selectionType: SelectionTypes
  showAdjacentDays: boolean
  showWeekNumber: boolean
  startDate: Date | number | string | null
  weekdayFormat: number | 'long' | 'narrow' | 'short'
  weekNumbersLabel: string | null
  yearFormat: 'numeric' | '2-digit'
}

const Default: CalendarConfig = {
  allowList: SVGAllowlist,
  ariaNavNextMonthLabel: 'Next month',
  ariaNavNextYearLabel: 'Next year',
  ariaNavNextYearsLabel: 'Next 12 years',
  ariaNavPrevMonthLabel: 'Previous month',
  ariaNavPrevYearLabel: 'Previous year',
  ariaNavPrevYearsLabel: 'Previous 12 years',
  ariaNothingToPickLabel: 'Nothing on this page can be picked',
  ariaSelectedDateAnnouncement: 'Selected date: {date}',
  ariaSelectedRangeAnnouncement: 'Selected range: {start} to {end}',
  ariaWeekNumberLabel: null,
  calendarDate: null,
  calendars: 1,
  dayFormat: 'numeric',
  disabledDates: null,
  endDate: null,
  firstDayOfWeek: 1,
  locale: 'default',
  maxDate: null,
  minDate: null,
  monthFormat: 'short',
  navNextDoubleIcon: CHEVRON_DOUBLE_RIGHT_ICON,
  navNextIcon: CHEVRON_RIGHT_ICON,
  navPrevDoubleIcon: CHEVRON_DOUBLE_LEFT_ICON,
  navPrevIcon: CHEVRON_LEFT_ICON,
  range: false,
  renderDayCell: null,
  renderMonthCell: null,
  renderQuarterCell: null,
  renderYearCell: null,
  sanitize: true,
  sanitizeFn: null,
  selectAdjacentDays: false,
  selectEndDate: false,
  selectionType: 'day',
  showAdjacentDays: true,
  showWeekNumber: false,
  startDate: null,
  weekdayFormat: 2,
  weekNumbersLabel: null,
  yearFormat: 'numeric'
}

const DefaultType: Record<string, string> = {
  allowList: 'object',
  ariaNavNextMonthLabel: 'string',
  ariaNavNextYearLabel: 'string',
  ariaNavNextYearsLabel: 'string',
  ariaNavPrevMonthLabel: 'string',
  ariaNavPrevYearLabel: 'string',
  ariaNavPrevYearsLabel: 'string',
  ariaNothingToPickLabel: '(string|null)',
  ariaSelectedDateAnnouncement: '(string|null)',
  ariaSelectedRangeAnnouncement: '(string|null)',
  ariaWeekNumberLabel: '(function|null)',
  calendarDate: '(date|number|string|null)',
  calendars: 'number',
  dayFormat: 'string',
  disabledDates: '(array|date|function|null)',
  endDate: '(date|number|string|null)',
  firstDayOfWeek: 'number',
  locale: 'string',
  maxDate: '(date|number|string|null)',
  minDate: '(date|number|string|null)',
  monthFormat: 'string',
  navNextDoubleIcon: 'string',
  navNextIcon: 'string',
  navPrevDoubleIcon: 'string',
  navPrevIcon: 'string',
  range: 'boolean',
  renderDayCell: '(function|null)',
  renderMonthCell: '(function|null)',
  renderQuarterCell: '(function|null)',
  renderYearCell: '(function|null)',
  sanitize: 'boolean',
  sanitizeFn: '(null|function)',
  selectAdjacentDays: 'boolean',
  selectEndDate: 'boolean',
  selectionType: 'string',
  showAdjacentDays: 'boolean',
  showWeekNumber: 'boolean',
  startDate: '(date|number|string|null)',
  weekdayFormat: '(number|string)',
  weekNumbersLabel: '(string|null)',
  yearFormat: 'string'
}

/**
 * Class definition
 */

class Calendar extends BaseComponent {
  declare ['constructor']: typeof Calendar
  protected declare _calendarDate: Date
  protected declare _startDate: Date | null
  protected declare _endDate: Date | null
  protected declare _minDate: Date | null
  protected declare _maxDate: Date | null
  protected declare _hoverDate: Date | null
  protected declare _keptDay: KeptDay | null
  protected declare _selectEndDate: boolean
  protected declare _view: ViewTypes
  protected declare _formatter: ReturnType<typeof createDateFormatter>
  protected declare _announcements: Array<() => void>
  protected declare _selectionAnnouncement: (() => void) | null

  constructor(element?: string | Element | null, config?: Partial<CalendarConfig> | null) {
    super(element)

    this._formatter = createDateFormatter()
    this._announcements = []
    this._selectionAnnouncement = null
    this._config = this._getConfig(config)
    this._initializeDates()
    this._initializeView()
    this._createCalendar()
    this._addEventListeners()
  }

  // Getters
  static override get Default(): typeof Default {
    return Default
  }

  static override get DefaultType(): typeof DefaultType {
    return DefaultType
  }

  static override get NAME(): string {
    return NAME
  }

  // Public
  setCalendarDate(calendarDate: CalendarConfig['calendarDate']): void {
    this._setDates({ calendarDate })
  }

  setEndDate(endDate: CalendarConfig['endDate']): void {
    this._setDates({ endDate })
  }

  setRange(startDate: CalendarConfig['startDate'], endDate: CalendarConfig['endDate']): void {
    this._setDates({ endDate, startDate })
  }

  setSelectEndDate(selectEndDate: CalendarConfig['selectEndDate']): void {
    this._setDates({ selectEndDate })
  }

  setStartDate(startDate: CalendarConfig['startDate']): void {
    this._setDates({ startDate })
  }

  refresh(): void {
    this._updateCalendar()
  }

  override dispose(): void {
    for (const remove of this._announcements) {
      remove()
    }

    this._element.replaceChildren()

    super.dispose()
  }

  // Private
  _setDates(values: Partial<Pick<CalendarConfig, 'calendarDate' | 'endDate' | 'selectEndDate' | 'startDate'>>): void {
    this._typeCheckConfig(values, Object.fromEntries(Object.keys(values).map(name => [name, DefaultType[name as keyof typeof DefaultType]])))
    this._initializeDates(values)
    this._updateCalendar()
  }

  _setAnnouncements(announcements: Pick<CalendarConfig, 'ariaSelectedDateAnnouncement' | 'ariaSelectedRangeAnnouncement'>): void {
    this._config = this._getConfig({ ...this._config, ...announcements })
  }

  _focusOnFirstAvailableCell(index: number): void {
    const panel = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)[index] as ParentNode
    const target = (SelectorEngine.findOne(this._rovingSelector(), panel) ??
      SelectorEngine.findOne('[tabindex="0"]', panel) ??
      SelectorEngine.findOne('[tabindex="0"]', this._element as ParentNode)) as HTMLElement | null

    if (target) {
      target.focus()
    }
  }

  _focusOnDate(date: Date): void {
    const targets = SelectorEngine.find(this._rovingSelector(), this._element as ParentNode) as HTMLElement[]
    const target = getClosestSelectable(targets, getStartOfView(date, this._view), this._rowsAreTargets()) ??
      SelectorEngine.findOne('table[tabindex="0"]', this._element as ParentNode)

    if (target) {
      target.focus()
    }
  }

  _focusOnCell(date: Date, panel?: number): void {
    const scope = panel === undefined ? this._element : SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)[panel]
    const matches = (SelectorEngine.find(this._rovingSelector(), scope as ParentNode) as HTMLElement[])
      .filter(element => this._getDate(element).toDateString() === date.toDateString())

    const inMonth = matches.find(element => {
      const cell = this._rowsAreTargets() ? SelectorEngine.findOne(SELECTOR_CALENDAR_CELL, element) : element
      return !cell?.classList.contains('previous') && !cell?.classList.contains('next')
    })

    const cell = inMonth ?? matches[0]

    if (cell) {
      cell.focus()
    }
  }

  _getEventTarget(event: any): HTMLElement | null {
    return event.target.closest(SELECTOR_CALENDAR_CELL) ??
      event.target.closest(SELECTOR_CALENDAR_ROW)
  }

  _rowsAreTargets(): boolean {
    return this._config.selectionType === 'week' && this._view === 'days'
  }

  _cellUnit(): ViewTypes | 'weeks' {
    return this._rowsAreTargets() ? 'weeks' : this._view
  }

  _getDate(target: HTMLElement): Date {
    if (this._rowsAreTargets()) {
      const firstCell = SelectorEngine.findOne(SELECTOR_CALENDAR_CELL, target.closest(SELECTOR_CALENDAR_ROW) as ParentNode)
      return getStartOfWeek(parseToDateString(Manipulator.getDataAttribute(firstCell as HTMLElement, 'date') as string), this._config.firstDayOfWeek)
    }

    return parseToDateString(Manipulator.getDataAttribute(target, 'date') as string)
  }

  _getPickedDate(target: HTMLElement): Date {
    const date = this._getDate(target)
    return this._rowsAreTargets() ? getWeekRowDate(date) : date
  }

  _handleCalendarClick(event: any): void {
    const target = this._getEventTarget(event)

    if (!target) {
      return
    }

    const date = this._getPickedDate(target)
    const cloneDate = new Date(date)
    const index = Manipulator.getDataAttribute(target.closest(SELECTOR_CALENDAR) as HTMLElement, 'calendar-index') as number

    if (isCellDisabled(date, this._cellUnit(), this._minDate, this._maxDate, this._config.disabledDates)) {
      return
    }

    if (this._view === 'months' && this._config.selectionType !== 'month') {
      this._setCalendarDate(index ? new Date(cloneDate.setMonth(cloneDate.getMonth() - index)) : date, 'days')
      this._setCalendarView('days', 'cellClick')
      this._updateCalendar(() => this._focusOnFirstAvailableCell(index))
      return
    }

    if (this._view === 'years' && this._config.selectionType !== 'year') {
      this._setCalendarDate(index ? new Date(cloneDate.setFullYear(cloneDate.getFullYear() - index)) : date, 'months')
      this._setCalendarView(this._config.selectionType === 'quarter' ? 'quarters' : 'months', 'cellClick')
      this._updateCalendar(() => this._focusOnFirstAvailableCell(index))
      return
    }

    const visible = this._element.checkVisibility()

    this._hoverDate = null
    this._selectDate(date)
    this._announceSelection(visible)
    this._updateClassNamesAndAriaLabels()
    this._updateCellContent()
    this._updateRovingTabIndex(SelectorEngine.findOne(':focus', this._element as ParentNode) as HTMLElement)
  }

  _handleCalendarKeydown(event: any): void {
    const target = this._getEventTarget(event) as HTMLElement
    const action = getCalendarKeyAction(event, this._getDate(target), this._getKeyContext(target))
    this._keptDay = getKeptDay(event.key, action, this._keptDay)

    if (!action) {
      return
    }

    event.preventDefault()

    if (action.type === 'activate') {
      this._handleCalendarClick(event)
      return
    }

    if (action.type === 'page' && action.date) {
      const target = action.date
      this._modifyCalendarDate(action.years, action.months, () => this._focusOnDate(target))
      return
    }

    this._moveFocus(action)
  }

  _handleGridKeydown(event: any): void {
    if (!event.target.matches('table')) {
      return
    }

    const context = this._getKeyContext(event.target)
    const action = getCalendarKeyAction(event, null, context)
    this._keptDay = getKeptDay(event.key, action, this._keptDay)

    if (!action) {
      return
    }

    event.preventDefault()

    if (action.type !== 'page') {
      this._moveFocus(action)
      return
    }

    this._modifyCalendarDate(action.years, action.months, () => {
      const panel = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)[context.panel]
      const stop = (SelectorEngine.findOne('[tabindex="0"]', panel as ParentNode) ??
        SelectorEngine.findOne('[tabindex="0"]', this._element as ParentNode)) as HTMLElement | null

      if (stop) {
        stop.focus()
      }
    })
  }

  _getKeyContext(target: HTMLElement): CalendarKeyContext {
    return {
      calendarDate: this._calendarDate,
      calendars: this._config.calendars,
      firstDayOfWeek: this._config.firstDayOfWeek,
      keptDay: this._keptDay,
      maxDate: this._maxDate,
      minDate: this._minDate,
      panel: SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode).indexOf(target.closest(SELECTOR_CALENDAR) as HTMLElement),
      rows: this._rowsAreTargets(),
      rtl: isRTL(this._element),
      view: this._view
    }
  }

  _moveFocus(action: CalendarKeyAction): void {
    if (action.type !== 'move') {
      return
    }

    if (action.years || action.months) {
      this._modifyCalendarDate(action.years, action.months, () => this._focusOnCell(action.date))
      return
    }

    this._focusOnCell(action.date, action.panel)
  }

  _handleCalendarMouseEnter(event: any): void {
    const target = this._getEventTarget(event)

    if (!target) {
      return
    }

    const date = this._getPickedDate(target)

    if (isCellDisabled(date, this._cellUnit(), this._minDate, this._maxDate, this._config.disabledDates)) {
      return
    }

    this._hoverDate = setTimeFromDate(date, this._selectEndDate ? this._endDate : this._startDate)
    this._updateRangeHover()
  }

  _handleCalendarMouseLeave(): void {
    this._hoverDate = null
    this._updateRangeHover()
  }

  _updateRangeHover(): void {
    if (this._selectEndDate ? this._startDate : this._endDate) {
      this._updateClassNamesAndAriaLabels()
    }
  }

  _addEventListeners(): void {
    const focusable = `${SELECTOR_CALENDAR_CELL_FOCUSABLE}, ${SELECTOR_CALENDAR_ROW_FOCUSABLE}`
    const selectable = `${SELECTOR_CALENDAR_CELL_SELECTABLE}, ${SELECTOR_CALENDAR_ROW_SELECTABLE}`

    EventHandler.on(this._element, EVENT_CLICK_DATA_API, () => {
      this._keptDay = null
    })

    EventHandler.on(this._element, EVENT_CLICK_DATA_API, focusable, event => {
      this._handleCalendarClick(event)
    })

    EventHandler.on(this._element, EVENT_KEYDOWN, focusable, event => {
      this._handleCalendarKeydown(event)
    })

    EventHandler.on(this._element, EVENT_MOUSEENTER, selectable, event => {
      this._handleCalendarMouseEnter(event)
    })

    EventHandler.on(this._element, EVENT_MOUSELEAVE, selectable, () => {
      this._handleCalendarMouseLeave()
    })

    EventHandler.on(this._element, EVENT_FOCUS, focusable, event => {
      this._updateRovingTabIndex(this._getEventTarget(event) as HTMLElement)
      this._handleCalendarMouseEnter(event)
    })

    EventHandler.on(this._element, EVENT_BLUR, focusable, () => {
      this._handleCalendarMouseLeave()
      this._updateRovingTabIndex()
    })

    EventHandler.on(this._element, EVENT_KEYDOWN, 'table[tabindex]', event => {
      this._handleGridKeydown(event)
    })

    this._addNavigationEventListeners()

    EventHandler.on(this._element, EVENT_MOUSELEAVE, 'table', () => {
      EventHandler.trigger(this._element, EVENT_CALENDAR_MOUSE_LEAVE)
    })
  }

  _addNavigationEventListeners(): void {
    const navigationSelectors = {
      [SELECTOR_BTN_PREV]: () => this._modifyCalendarDate(0, -1),
      [SELECTOR_BTN_DOUBLE_PREV]: () => this._modifyCalendarDate(this._view === 'years' ? -YEARS_PER_PAGE : -1),
      [SELECTOR_BTN_NEXT]: () => this._modifyCalendarDate(0, 1),
      [SELECTOR_BTN_DOUBLE_NEXT]: () => this._modifyCalendarDate(this._view === 'years' ? YEARS_PER_PAGE : 1),
      [SELECTOR_BTN_MONTH]: (index: number) => this._showPeriodView('months', index),
      [SELECTOR_BTN_YEAR]: (index: number) => this._showPeriodView(this._view === 'years' ? this._selectionView() : 'years', index)
    }

    for (const [selector, handler] of Object.entries(navigationSelectors)) {
      EventHandler.on(this._element, EVENT_CLICK_DATA_API, selector, (event: any) => {
        event.preventDefault()
        const index = SelectorEngine.find(selector, this._element).indexOf(event.target.closest(selector))

        if (!handler(index)) {
          SelectorEngine.find(selector, this._element)[index]?.focus()
        }
      })
    }
  }

  _setCalendarDate(date: any, view: string = this._view): void {
    this._calendarDate = date

    EventHandler.trigger(this._element, EVENT_CALENDAR_DATE_CHANGE, {
      date,
      view
    })
  }

  _setCalendarView(view: ViewTypes, source?: string): void {
    this._view = view

    EventHandler.trigger(this._element, EVENT_CALENDAR_VIEW_CHANGE, {
      view,
      source
    })
  }

  _showPeriodView(view: ViewTypes, index: number): boolean {
    const shown = getCalendarDate(this._calendarDate, index, this._view === 'years' ? view : this._view)
    const start = getStartOfView(createDate(shown.getFullYear(), shown.getMonth(), 1), view).toDateString()
    const focus = view !== this._view && this._element.contains(document.activeElement)

    this._setCalendarView(view, 'navigation')
    this._updateCalendar(() => {
      if (focus) {
        const panel = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)[index]
        const target = SelectorEngine.find(`[data-coreui-date="${start}"]`, this._element)
          .map(cell => cell.closest<HTMLElement>(this._rovingSelector()))
          .find(Boolean) ?? SelectorEngine.findOne('[tabindex="0"]', panel)
        target?.focus()
      }
    })

    return focus
  }

  _modifyCalendarDate(years: number, months = 0, callback?: () => void): void {
    const year = this._calendarDate.getFullYear() + years
    const month = this._calendarDate.getMonth() + months
    this._setCalendarDate(createDate(year, month, 1))
    this._updateCalendar(callback)
  }

  _setEndDate(date: Date | null): void {
    if (date === this._endDate) {
      return
    }

    this._endDate = setTimeFromDate(date, this._endDate)
    EventHandler.trigger(this._element, EVENT_END_DATE_CHANGE, {
      date: getDateBySelectionType(this._endDate, this._config.selectionType),
      dateObject: this._endDate
    })
  }

  _setStartDate(date: Date | null): void {
    if (date === this._startDate) {
      return
    }

    this._startDate = setTimeFromDate(date, this._startDate)
    EventHandler.trigger(this._element, EVENT_START_DATE_CHANGE, {
      date: getDateBySelectionType(this._startDate, this._config.selectionType),
      dateObject: this._startDate
    })
  }

  _setSelectEndDate(value: boolean): void {
    this._selectEndDate = value
    EventHandler.trigger(this._element, EVENT_SELECT_END_CHANGE, {
      value
    })
  }

  _selectDate(date: any): void {
    if (isCellDisabled(date, this._cellUnit(), this._minDate, this._maxDate, this._config.disabledDates)) {
      return
    }

    if (this._config.range) {
      const start = convertToSelectionDate(this._startDate, this._config.selectionType)
      const end = convertToSelectionDate(this._endDate, this._config.selectionType)

      if (this._selectEndDate) {
        if (start && start > date) {
          this._setStartDate(date)
          this._setEndDate(null)
          return
        }

        this._setSelectEndDate(false)

        if (isDisableDateInRange(start, date, this._config.disabledDates, this._cellUnit())) {
          this._setStartDate(null)
          this._setEndDate(null)
          return
        }

        this._setEndDate(date)
        return
      }

      if (end && end < date) {
        this._setStartDate(date)
        this._setEndDate(null)
        this._setSelectEndDate(true)
        return
      }

      if (isDisableDateInRange(date, end, this._config.disabledDates, this._cellUnit())) {
        this._setStartDate(null)
        this._setEndDate(null)
        return
      }

      this._setSelectEndDate(true)
      this._setStartDate(date)
      return
    }

    this._setStartDate(date)
  }

  _createCalendarPanel(order: number): HTMLElement {
    const calendarPanelEl = document.createElement('div')
    calendarPanelEl.classList.add('calendar')

    Manipulator.setDataAttribute(calendarPanelEl, 'calendar-index', order)

    calendarPanelEl.innerHTML = `<div class="calendar-nav"><div class="calendar-nav-prev"></div><div class="calendar-nav-date"></div><div class="calendar-nav-next"></div></div><table role="grid"></table><span id="${getUID('calendar-description')}" hidden></span>`
    this._renderCalendarPanel(calendarPanelEl, order)

    return calendarPanelEl
  }

  _renderCalendarPanel(panel: HTMLElement, order: number): void {
    const calendarDate = getCalendarDate(this._calendarDate, order, this._view)
    const days = this._view === 'days'
    const years = this._view === 'years'
    const [navigation, calendarTable] = panel.children
    const [prev, region, next] = navigation.children
    const monthLabel = days ? this._formatDate(calendarDate, { month: 'long' }) : ''
    const yearLabel = years ? formatYearsRange(calendarDate.getFullYear(), this._config.locale) : this._formatDate(calendarDate, { year: 'numeric' })
    const gridLabel = this._gridLabel(calendarDate)
    const turned = calendarTable.getAttribute('aria-label') !== gridLabel

    prev.innerHTML = `${this._navButton('btn-double-prev', 'navPrevDoubleIcon', years ? this._config.ariaNavPrevYearsLabel : this._config.ariaNavPrevYearLabel)} ${days ? this._navButton('btn-prev', 'navPrevIcon', this._config.ariaNavPrevMonthLabel) : ''}`

    next.innerHTML = `${days ? this._navButton('btn-next', 'navNextIcon', this._config.ariaNavNextMonthLabel) : ''} ${this._navButton('btn-double-next', 'navNextDoubleIcon', years ? this._config.ariaNavNextYearsLabel : this._config.ariaNavNextYearLabel)}`
    calendarTable.setAttribute('aria-label', gridLabel)

    if (this._picksRange()) {
      calendarTable.setAttribute('aria-multiselectable', 'true')
    } else {
      calendarTable.removeAttribute('aria-multiselectable')
    }

    calendarTable.innerHTML = days ? this._daysHtml(calendarDate) : this._periodsHtml(calendarDate)

    if (turned) {
      const month = days ? `<button type="button" class="calendar-nav-btn btn-month">${monthLabel}</button>` : ''
      const year = `<button type="button" class="calendar-nav-btn btn-year">${yearLabel}</button>`
      region.innerHTML = isYearBeforeMonth(this._config.locale) ? `${year} ${month}` : `${month} ${year}`
    }

    (SelectorEngine.findOne(SELECTOR_BTN_YEAR, region) as HTMLButtonElement).disabled = years && this._selectionView() === 'years'

    this._describeGrid(panel)
  }

  _describeGrid(panel: Element): void {
    const [navigation, grid, description] = panel.children
    const region = navigation.children[1]
    const label = this._config.ariaNothingToPickLabel
    const note = SelectorEngine.findOne(`.${CLASS_NAME_VISUALLY_HIDDEN}`, region)

    description.textContent = label

    if (!label || SelectorEngine.findOne('[data-coreui-selectable]', grid)) {
      grid.removeAttribute('aria-describedby')
      note?.remove()
      return
    }

    grid.setAttribute('aria-describedby', description.id)

    if (!note) {
      region.insertAdjacentHTML('beforeend', `<span class="${CLASS_NAME_VISUALLY_HIDDEN}">${escapeHtml(label)}</span>`)
    } else if (note.textContent !== label) {
      note.textContent = label
    }
  }

  _pages(): Array<[string | null, boolean]> {
    return SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode).map(panel => {
      const grid = panel.children[1]
      return [grid.getAttribute('aria-label'), grid.hasAttribute('aria-describedby')]
    })
  }

  _announceSelection(visible: boolean): void {
    this._selectionAnnouncement?.()
    this._selectionAnnouncement = null

    const { ariaSelectedDateAnnouncement, ariaSelectedRangeAnnouncement, range } = this._config
    const start = this._startDate
    const end = range ? this._endDate : start

    if (!visible || !start || !end) {
      return
    }

    const startName = this._selectionName(start)
    const endName = this._selectionName(end)
    const message = startName === endName ?
      ariaSelectedDateAnnouncement?.replaceAll('{date}', () => startName) :
      ariaSelectedRangeAnnouncement?.replaceAll('{start}', () => startName).replaceAll('{end}', () => endName)

    if (message) {
      this._selectionAnnouncement = announce(message, { context: this._element })
    }
  }

  _selectionName(date: Date): string {
    const { ariaWeekNumberLabel, firstDayOfWeek, locale, selectionType, showWeekNumber } = this._config

    return formatSelectionName(date, {
      ariaWeekNumberLabel, firstDayOfWeek, locale, selectionType, showWeekNumber
    })
  }

  _announcePages(pages: Array<[string | null, boolean]>): void {
    if (!this._element.isConnected || !this._element.checkVisibility()) {
      return
    }

    const panels = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)
    const active = (this._element.getRootNode() as Document | ShadowRoot).activeElement
    const date = active && this._element.contains(active) ?
      active.closest(`${SELECTOR_CALENDAR_CELL}[aria-label], ${SELECTOR_CALENDAR_ROW}[aria-label]`) :
      null
    const messages = this._pages().flatMap(([label, nothing], index) => {
      const [previousLabel, previousNothing] = pages[index] ?? []

      if ((label === previousLabel && (previousNothing || !nothing)) || (date && !nothing)) {
        return []
      }

      return [[date && panels[index].contains(date) ? '' : label, nothing && this._config.ariaNothingToPickLabel].filter(Boolean).join(', ')]
    })

    if (messages.length > 0) {
      for (const remove of this._announcements) {
        remove()
      }

      this._announcements = messages.map(message => announce(message, { context: this._element }))
    }
  }

  _daysHtml(calendarDate: Date): string {
    const { ariaWeekNumberLabel, showAdjacentDays, showWeekNumber, weekdayFormat, weekNumbersLabel } = this._config
    const weeks = getMonthDetails(calendarDate.getFullYear(), calendarDate.getMonth(), this._config.firstDayOfWeek)
    const weekLabel = getWeekLabel(this._config.locale)
    const headerCell = (content: string, abbr = '') => `<th class="${CLASS_NAME_CALENDAR_CELL}"${abbr}><div class="calendar-header-cell-inner">${content}</div></th>`

    const weekdays = weeks[0].days.map(({ date }) => {
      const long = this._formatDate(date, { weekday: 'long' })
      return headerCell(typeof weekdayFormat === 'string' ? this._formatDate(date, { weekday: weekdayFormat as 'long' }) : long.slice(0, weekdayFormat), ` abbr="${long}"`)
    })

    const rows = weeks.map(({ week, days }) => {
      const visible = showAdjacentDays || days.some(({ month }) => month === 'current')
      const attributes = this._rowWeekAttributes(days[0].date, visible)
      const cells = days.map(({ date, month }) => month === 'current' || showAdjacentDays ?
        this._cellHtml(date, this._cellDayAttributes(date, month, attributes.meta), this._formatDate(date, { day: this._config.dayFormat })) :
        '<td role="gridcell"></td>')
      const weekNumberName = showWeekNumber ? getWeekNumberName(week.number, weekLabel, ariaWeekNumberLabel) : ''
      const rowName = () => `${showWeekNumber ? `${weekNumberName}, ` : ''}${formatWeekName(days, this._config.locale)}`
      const ariaLabel = this._config.selectionType === 'week' && visible ? ` aria-label="${escapeHtml(rowName())}"` : ''

      return `<tr class="${attributes.className}"${this._stateHtml(attributes)}${ariaLabel}>${showWeekNumber ? `<th class="calendar-cell-week-number" scope="row" aria-label="${escapeHtml(weekNumberName)}">${week.number}</th>` : ''}${cells.join('')}</tr>`
    })

    const weekNumberHeader = weekNumbersLabel ? escapeHtml(weekNumbersLabel) : `<span class="${CLASS_NAME_VISUALLY_HIDDEN}">${escapeHtml(weekLabel)}</span>`

    return `<thead><tr>${showWeekNumber ? headerCell(weekNumberHeader) : ''}${weekdays.join('')}</tr></thead><tbody>${rows.join('')}</tbody>`
  }

  _periodsHtml(calendarDate: Date): string {
    const view = this._view
    const year = calendarDate.getFullYear()
    const monthNames = view === 'months' ? getMonthsNames(this._config.locale, this._config.monthFormat) : []
    const dates = view === 'years' ?
      getYears(year).map(value => createDate(value)) :
      Array.from({ length: view === 'months' ? 12 : 4 }, (_, index) => createDate(year, index * (view === 'months' ? 1 : 3), 1))

    const label = (date: Date) => {
      if (view === 'months') {
        return monthNames[date.getMonth()]
      }

      return view === 'quarters' ? `Q${(date.getMonth() / 3) + 1}` : this._formatDate(date, { year: this._config.yearFormat })
    }

    const rows = createGroupsInArray(dates, view === 'quarters' ? 1 : 4)
      .map(row => `<tr>${row.map(date => this._cellHtml(date, this._cellPeriodAttributes(date), label(date))).join('')}</tr>`)

    return `<tbody>${rows.join('')}</tbody>`
  }

  _cellHtml(date: Date, attributes: Record<string, any>, label: string): string {
    const renderer = CELL_RENDERERS[this._view]
    const content = this._config[renderer] ? this._cellContent(date, attributes.meta) : label
    const ariaLabel = attributes.ariaLabel && !this._config[renderer] ? ` aria-label="${escapeHtml(attributes.ariaLabel)}"` : ''
    const ariaCurrent = attributes.ariaCurrent ? ' aria-current="date"' : ''

    return `<td class="${attributes.className}" role="gridcell"${this._stateHtml(attributes)}${ariaCurrent}${ariaLabel} data-coreui-date="${date.toDateString()}"><div class="${CLASS_NAME_CALENDAR_CELL_INNER} ${this._view.slice(0, -1)}">${content}</div></td>`
  }

  _cellContent(date: Date, meta: Record<string, boolean>): string {
    return sanitizeByConfig(this._config[CELL_RENDERERS[this._view]](date, { ...meta, label: this._cellName(date) }), this._config)
  }

  _stateHtml({ ariaDisabled, ariaSelected, focusable, selectable }: Record<string, any>): string {
    return `${focusable ? ' tabindex="-1"' : ''}${selectable ? ' data-coreui-selectable' : ''}${ariaSelected ? ' aria-selected="true"' : ''}${ariaDisabled ? ' aria-disabled="true"' : ''}`
  }

  _updateRovingTabIndex(preferred?: HTMLElement): void {
    setRovingTabIndex(this._element as HTMLElement, this._rovingSelector(), getStartOfView(this._calendarDate as Date, this._view), this._rowsAreTargets(), preferred)
  }

  _gridLabel(date: Date): string {
    if (this._view === 'days') {
      return this._formatDate(date, { month: 'long', year: 'numeric' })
    }

    if (this._view === 'years') {
      return formatYearsRange(date.getFullYear(), this._config.locale)
    }

    return this._formatDate(date, { year: 'numeric' })
  }

  _rovingSelector(): string {
    return this._rowsAreTargets() ? SELECTOR_CALENDAR_ROW_FOCUSABLE : SELECTOR_CALENDAR_CELL_FOCUSABLE
  }

  _createCalendar(): void {
    this._setCalendarClasses()

    for (const [index, _] of Array.from({ length: this._config.calendars }).entries()) {
      this._element.append(this._createCalendarPanel(index))
    }

    this._updateRovingTabIndex()
  }

  _setCalendarClasses(): void {
    const { selectionType, showWeekNumber } = this._config

    for (const type of ['day', 'month', 'quarter', 'week', 'year']) {
      if (type !== selectionType) {
        this._element.classList.remove(`select-${type}`)
      }
    }

    if (selectionType && this._view === 'days') {
      this._element.classList.add(`select-${selectionType}`)
    }

    this._element.classList.toggle(CLASS_NAME_SHOW_WEEK_NUMBERS, Boolean(showWeekNumber))

    this._element.classList.add(CLASS_NAME_CALENDARS)
  }

  _initializeDates(values?: Partial<CalendarConfig>): void {
    const given: Partial<CalendarConfig> = values ?? this._config
    const changed = (...names: string[]) => !values || names.some(name => name in values)

    if (!values) {
      this._minDate = convertToLimitDate(this._config.minDate, this._config.selectionType)
      this._maxDate = convertToLimitDate(this._config.maxDate, this._config.selectionType)
    }

    if (changed('calendarDate', 'startDate', 'endDate')) {
      const source: any = values ?
        (['calendarDate', 'startDate', 'endDate'] as const).filter(name => name in values).map(name => values[name]).find(Boolean) ?? null :
        given.calendarDate || given.startDate || given.endDate

      const date = source === given.calendarDate ?
        convertToDateObject(source, this._config.selectionType) :
        convertToShownDate(source, this._config.selectionType, this._config.firstDayOfWeek)

      this._calendarDate = date || this._calendarDate || constrainDate(new Date(), this._minDate, this._maxDate)
    }

    if (changed('startDate')) {
      this._startDate = convertToDateObject(given.startDate, this._config.selectionType)
    }

    if (changed('endDate')) {
      this._endDate = convertToDateObject(given.endDate, this._config.selectionType)
    }

    if (changed('selectEndDate')) {
      this._selectEndDate = Boolean(given.selectEndDate)
    }

    this._hoverDate = null
  }

  _initializeView(): void {
    this._view = this._selectionView()
  }

  _picksRange(): boolean {
    return this._config.range && this._selectionView() === this._view
  }

  _selectionView(): ViewTypes {
    return VIEW_BY_SELECTION_TYPE[this._config.selectionType] || 'days'
  }

  _updateCalendar(callback?: () => void): void {
    const focused = this._element.contains(document.activeElement) ? document.activeElement as HTMLElement : null
    const restoreFocus = focused && this._focusRestorer(focused)
    const panels = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)
    const pages = this._pages()

    if (panels.length === this._config.calendars) {
      this._setCalendarClasses()

      for (const [index, panel] of panels.entries()) {
        this._renderCalendarPanel(panel as HTMLElement, index)
      }

      this._updateRovingTabIndex()
    } else {
      this._element.replaceChildren()
      this._createCalendar()
    }

    if (callback) {
      callback()
    }

    if (restoreFocus && (!document.activeElement || document.activeElement === document.body)) {
      restoreFocus()
    }

    this._announcePages(pages)
  }

  _focusRestorer(focused: HTMLElement): () => void {
    const index = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode).indexOf(focused.closest(SELECTOR_CALENDAR) as HTMLElement)
    const cell = focused.matches(SELECTOR_CALENDAR_ROW) ? SelectorEngine.findOne(SELECTOR_CALENDAR_CELL, focused) : focused.closest(SELECTOR_CALENDAR_CELL)
    const date = cell?.getAttribute('data-coreui-date')
    const { className } = focused

    return () => {
      const panel = SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)[index] ?? this._element
      const matches = SelectorEngine.find(this._rovingSelector(), this._element as ParentNode).filter(target =>
        (target.matches(SELECTOR_CALENDAR_ROW) ? SelectorEngine.findOne(SELECTOR_CALENDAR_CELL, target) : target)?.getAttribute('data-coreui-date') === date)
      const target = ((date ? matches.find(target => !target.matches('.next, .previous')) ?? matches[0] : panel.getElementsByClassName(className)[0]) ??
        SelectorEngine.findOne('[tabindex="0"]', panel) ?? SelectorEngine.findOne('[tabindex="0"]', this._element)) as HTMLElement | null

      target?.focus({ preventScroll: true })
    }
  }

  _updateClassNamesAndAriaLabels(): void {
    const pages = this._pages()

    if (this._rowsAreTargets()) {
      for (const row of SelectorEngine.find(SELECTOR_CALENDAR_ROW, this._element as ParentNode)) {
        const firstCell = SelectorEngine.findOne(SELECTOR_CALENDAR_CELL, row)

        if (firstCell) {
          this._applyState(row, this._rowWeekAttributes(this._getDate(firstCell)))
        }
      }
    } else {
      for (const cell of SelectorEngine.find(`${SELECTOR_CALENDAR_CELL}[data-coreui-date]`, this._element as ParentNode)) {
        const date = this._getDate(cell)

        this._applyState(cell, this._view === 'days' ?
          this._cellDayAttributes(date, ['previous', 'next'].find(month => cell.classList.contains(month)) ?? 'current') :
          this._cellPeriodAttributes(date))
      }
    }

    for (const panel of SelectorEngine.find(SELECTOR_CALENDAR, this._element as ParentNode)) {
      this._describeGrid(panel)
    }

    this._announcePages(pages)
  }

  _updateCellContent(): void {
    if (!this._config[CELL_RENDERERS[this._view]]) {
      return
    }

    const focused = this._element.contains(document.activeElement) ? document.activeElement as HTMLElement : null
    const restoreFocus = focused && this._focusRestorer(focused)

    for (const cell of SelectorEngine.find(`${SELECTOR_CALENDAR_CELL}[data-coreui-date]`, this._element as ParentNode)) {
      const date = parseToDateString(Manipulator.getDataAttribute(cell, 'date') as string)
      const attributes = this._view === 'days' ?
        this._cellDayAttributes(date, ['previous', 'next'].find(month => cell.classList.contains(month)) ?? 'current', this._rowWeekAttributes(this._getDate(cell)).meta) :
        this._cellPeriodAttributes(date)
      const inner = SelectorEngine.findOne(`.${CLASS_NAME_CALENDAR_CELL_INNER}`, cell) as HTMLElement
      const content = this._cellContent(date, attributes.meta)

      if (inner.innerHTML !== content) {
        inner.innerHTML = content
      }
    }

    if (restoreFocus && (!document.activeElement || document.activeElement === document.body)) {
      restoreFocus()
    }
  }

  _applyState(element: HTMLElement, { ariaDisabled, ariaSelected, className, selectable }: Record<string, any>): void {
    element.className = className
    element.toggleAttribute('data-coreui-selectable', selectable)

    if (ariaSelected) {
      element.setAttribute('aria-selected', 'true')
    } else {
      element.removeAttribute('aria-selected')
    }

    if (ariaDisabled) {
      element.setAttribute('aria-disabled', 'true')
    } else {
      element.removeAttribute('aria-disabled')
    }
  }

  _isRangeHover(inRange: (start: Date | null, end: Date | null) => boolean): boolean {
    return Boolean(this._hoverDate) && (this._selectEndDate ? inRange(this._startDate, this._hoverDate) : inRange(this._hoverDate, this._endDate))
  }

  _classNames(classNames: any): string {
    return Object.entries(classNames)
      .filter(([_, value]) => Boolean(value))
      .map(([key]) => key)
      .join(' ')
  }

  _cellDayAttributes(date: Date, month: string, row?: Record<string, boolean>): Record<string, any> {
    const isCurrentMonth = month === 'current'

    const isFiller = this._config.selectionType === 'day' && !isCurrentMonth && !this._config.selectAdjacentDays
    const isDisabled = isDateDisabled(date, this._minDate, this._maxDate, this._config.disabledDates)
    const isSelected = isDateSelected(date, this._startDate, this._endDate)
    const isTodayDate = isCurrentMonth && isToday(date)
    const isInRange = !isFiller && isDateInRange(date, this._startDate, this._endDate)
    const meta = {
      isDisabled: isDisabled || isFiller,
      isInCurrentMonth: isCurrentMonth,
      isInRange,
      isSelected: isSelected && !isFiller,
      isToday: isTodayDate,
      ...row
    }

    if (this._config.selectionType !== 'day' || this._view !== 'days') {
      return {
        className: this._classNames({
          [CLASS_NAME_CALENDAR_CELL]: true,
          today: isTodayDate,
          [month]: true
        }),
        focusable: false,
        selectable: false,
        ariaSelected: false,
        ariaLabel: this._cellName(date),
        ariaCurrent: isTodayDate,
        meta
      }
    }

    const isRangeHover = !isFiller && this._isRangeHover((start, end) => isDateInRange(date, start, end))

    const classNames = this._classNames({
      [CLASS_NAME_CALENDAR_CELL]: true,
      clickable: !isCurrentMonth && this._config.selectAdjacentDays,
      disabled: isDisabled,
      range: isInRange,
      'range-hover': isRangeHover,
      selected: isSelected,
      today: isTodayDate,
      [month]: true
    })

    return {
      className: classNames,
      focusable: !isFiller && !isCellOutsideLimits(date, 'days', this._minDate, this._maxDate),
      selectable: !isDisabled && !isFiller,
      ariaDisabled: isDisabled || isFiller,
      ariaSelected: !isFiller && (isSelected || (this._picksRange() && !isDisabled && isDateInRange(date, this._startDate, this._endDate))),
      ariaLabel: this._cellName(date),
      ariaCurrent: isTodayDate,
      meta
    }
  }

  _cellPeriodAttributes(date: Date): Record<string, any> {
    const view = this._view as PeriodViewTypes
    const { firstDayOfWeek, selectionType } = this._config
    const start = convertToShownDate(this._startDate, selectionType, firstDayOfWeek)
    const end = convertToShownDate(this._endDate, selectionType, firstDayOfWeek)
    const isDisabled = isPeriodDisabled(date, view, this._minDate, this._maxDate, this._config.disabledDates)
    const isSelected = isPeriodSelected(date, view, start, end)
    const isInRange = isPeriodInRange(date, view, start, end)
    const isRangeHover = VIEW_BY_SELECTION_TYPE[this._config.selectionType] === view &&
      this._isRangeHover((start, end) => isPeriodInRange(date, view, start, end))

    return {
      className: this._classNames({
        [CLASS_NAME_CALENDAR_CELL]: true,
        disabled: isDisabled,
        'range-hover': isRangeHover,
        range: isInRange,
        selected: isSelected
      }),
      focusable: !isCellOutsideLimits(date, view, this._minDate, this._maxDate),
      selectable: !isDisabled,
      ariaDisabled: isDisabled,
      ariaSelected: isSelected || (this._picksRange() && isInRange && !isDisabled),
      meta: {
        isDisabled,
        isInRange,
        isSelected
      }
    }
  }

  _rowWeekAttributes(date: Date, visible = true): Record<string, any> {
    if (this._config.selectionType !== 'week' || !visible) {
      return {
        className: this._classNames({ [CLASS_NAME_CALENDAR_ROW]: true }),
        focusable: false,
        selectable: false,
        ariaSelected: false
      }
    }

    const weekDate = getWeekRowDate(date)
    const isDisabled = isPeriodDisabled(weekDate, 'weeks', this._minDate, this._maxDate, this._config.disabledDates)
    const isSelected = isPeriodSelected(weekDate, 'weeks', this._startDate, this._endDate)
    const isInRange = isPeriodInRange(weekDate, 'weeks', this._startDate, this._endDate)

    const isRangeHover = this._isRangeHover((start, end) => isPeriodInRange(weekDate, 'weeks', start, end))

    const classNames = this._classNames({
      [CLASS_NAME_CALENDAR_ROW]: true,
      disabled: isDisabled,
      range: isInRange,
      'range-hover': isRangeHover,
      selected: isSelected
    })

    return {
      className: classNames,
      focusable: !isCellOutsideLimits(date, 'days', this._minDate, this._maxDate, true),
      selectable: !isDisabled,
      ariaDisabled: isDisabled,
      ariaSelected: isSelected || (this._picksRange() && isInRange && !isDisabled),
      meta: {
        isDisabled,
        isInRange,
        isSelected
      }
    }
  }

  _navButton(className: string, icon: string, label: string): string {
    return `<button type="button" class="calendar-nav-btn ${className}" aria-label="${escapeHtml(label)}"><span class="calendar-nav-icon">${this._navIcon(icon)}</span></button>`
  }

  _navIcon(name: string): string {
    const mirrored = {
      navNextDoubleIcon: 'navPrevDoubleIcon',
      navNextIcon: 'navPrevIcon',
      navPrevDoubleIcon: 'navNextDoubleIcon',
      navPrevIcon: 'navNextIcon'
    }

    return sanitizeByConfig(this._config[isRTL(this._element) ? (mirrored as Record<string, string>)[name] : name], this._config)
  }

  _formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return this._formatter(date, this._config.locale, options)
  }

  _cellName(date: Date): string {
    return formatCellName(date, this._view, (value, options) => this._formatDate(value, options))
  }

  // Static

  static calendarInterface(element: string | Element | null, config?: any, ...args: any[]): void {
    const data: any = Calendar.getOrCreateInstance(element, config)

    if (typeof config === 'string') {
      if (typeof data[config] === 'undefined') {
        throw new TypeError(`No method named "${config}"`)
      }

      data[config](...args)
    }
  }

  static jQueryInterface(this: any, config: any, ...args: any[]): any {
    return jQueryDispatch(this, Calendar, config, args)
  }
}

/**
 * Data API implementation
 */

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
  for (const element of Array.from(document.querySelectorAll(SELECTOR_DATA_CALENDAR))) {
    Calendar.calendarInterface(element)
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(Calendar)

export default Calendar
export type { CalendarCellMeta, CalendarConfig, CalendarDayCellMeta }
